"use client";
import React, { Suspense, useEffect, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Tabs } from "antd";
import { useIntl } from "react-intl";
import LegalContent from "../../Components/Legal/LegalContent";
import { getContactCategoriesAction } from "../../Actions/LogicActions/NeedHelp/GetContactCategoriesAction";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { IContactCategoryConfigResponse } from "../../Orchestrator/Types/Interface/ICustomerContact";
import { ContactDetailsEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { contactTelHref } from "@/Lib/Utils/phoneFormat";
import "./page.css";

const TAB_KEYS = {
  passengerCharter: "passenger-charter",
  termsOfUse: "terms-of-use",
  privacyPolicy: "privacy-policy",
} as const;

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

const TAB_FILES: Record<TabKey, string> = {
  [TAB_KEYS.passengerCharter]: "/legal/passenger-charter.html",
  [TAB_KEYS.termsOfUse]: "/legal/terms-of-use.html",
  [TAB_KEYS.privacyPolicy]: "/legal/privacy-policy.html",
};

const isValidTab = (value: string | null): value is TabKey =>
  value === TAB_KEYS.passengerCharter ||
  value === TAB_KEYS.termsOfUse ||
  value === TAB_KEYS.privacyPolicy;

const TermsAndConditionsContent = () => {
  const intl = useIntl();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [categoriesData] = useRecState<IContactCategoryConfigResponse>(
    "contactCategoriesAtom"
  );

  useEffect(() => {
    getContactCategoriesAction.execute();
  }, []);

  const activeTab: TabKey = useMemo(() => {
    const param = searchParams.get("tab");
    return isValidTab(param) ? param : TAB_KEYS.passengerCharter;
  }, [searchParams]);

  const handleTabChange = (key: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", key);
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const tabItems = [
    {
      key: TAB_KEYS.passengerCharter,
      label: intl.formatMessage({ id: "passengerCharter" }),
      // Preload every tab's document on mount so switching tabs is instant
      // (no per-tab fetch/flash).
      forceRender: true,
      children: (
        <div className="tnc-tab-content">
          <LegalContent src={TAB_FILES[TAB_KEYS.passengerCharter]} />
        </div>
      ),
    },
    {
      key: TAB_KEYS.termsOfUse,
      label: intl.formatMessage({ id: "termsOfUse" }),
      forceRender: true,
      children: (
        <div className="tnc-tab-content">
          <LegalContent src={TAB_FILES[TAB_KEYS.termsOfUse]} />
        </div>
      ),
    },
    {
      key: TAB_KEYS.privacyPolicy,
      label: intl.formatMessage({ id: "privacyPolicy" }),
      forceRender: true,
      children: (
        <div className="tnc-tab-content">
          <LegalContent src={TAB_FILES[TAB_KEYS.privacyPolicy]} />
        </div>
      ),
    },
  ];

  // WhatsApp and Live Chat hidden per business request (2026-06-11).
  const contactCards = [
    {
      icon: "/assets/contact/envelope.svg",
      name: intl.formatMessage({ id: "email" }),
      title: intl.formatMessage({ id: "termsAndConditions.email.title" }),
      description: categoriesData?.email || ContactDetailsEnum.EMAIL,
      url: `mailto:${
        categoriesData?.email || ContactDetailsEnum.EMAIL
      }?subject=Need%20help`,
    },
    // {
    //   icon: "/assets/contact/whats-app.svg",
    //   name: intl.formatMessage({ id: "whatsApp" }),
    //   title: intl.formatMessage({ id: "termsAndConditions.available24x7" }),
    //   description: categoriesData?.whatsapp || ContactDetailsEnum.WHATSAPP,
    //   url: `https://wa.me/${(
    //     categoriesData?.whatsapp || ContactDetailsEnum.WHATSAPP
    //   ).replace(/\D/g, "")}`,
    // },
    // {
    //   icon: "/assets/contact/message-text.svg",
    //   name: intl.formatMessage({ id: "contactLiveChat" }),
    //   title: intl.formatMessage({ id: "termsAndConditions.available24x7" }),
    //   description: intl.formatMessage({ id: "chatWithAgent" }),
    //   url: "#",
    // },
    {
      icon: "/assets/contact/phone.svg",
      name: intl.formatMessage({ id: "customerSupport" }),
      title: intl.formatMessage({ id: "termsAndConditions.support.hours" }),
      description: categoriesData?.phoneNumber || ContactDetailsEnum.PHONE,
      url: contactTelHref(categoriesData?.phoneNumber),
    },
  ];

  return (
    <div className="tnc-page">
      <div className="container">
        <h1 className="tnc-title">
          {intl.formatMessage({ id: "termsAndConditions" })}
        </h1>

        <section className="tnc-card tnc-tabs-card">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={tabItems}
            className="tnc-tabs"
            destroyInactiveTabPane={false}
          />
        </section>

        <section className="tnc-card tnc-contact-card">
          <h2 className="tnc-contact-title">
            {intl.formatMessage({ id: "contactCustomerSupport" })}
          </h2>
          <div className="tnc-contact-grid">
            {contactCards.map((card) => (
              <Link
                key={card.name}
                href={card.url}
                className="tnc-contact-item"
                target={card.url.startsWith("http") ? "_blank" : undefined}
                rel={
                  card.url.startsWith("http") ? "noopener noreferrer" : undefined
                }
              >
                <Image
                  src={card.icon}
                  alt={card.name}
                  width={48}
                  height={48}
                  className="tnc-contact-icon"
                />
                <div className="tnc-contact-text">
                  <div className="tnc-contact-row">
                    <span className="tnc-contact-name">{card.name}</span>
                    <span className="tnc-contact-subtitle">{card.title}</span>
                  </div>
                  <span className="tnc-contact-link">{card.description}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const TermsAndConditionsPage = () => (
  <Suspense fallback={<div className="tnc-page" />}>
    <TermsAndConditionsContent />
  </Suspense>
);

export default TermsAndConditionsPage;
